import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Grid } from '@mui/system';
import { isEmpty } from 'lodash';
import {
  Company,
  type Scenario,
  SpreadsheetNote,
} from 'api';
import { WATERFALL_SLUG } from 'common/constants/allocations';
import { CAP_TABLE_CURRENCY_PAGE } from 'common/constants/currencyPageTypes';
import { CAP_TABLE_REFERENCE_TITLE, CAP_TABLE_REFERENCE_TYPE } from 'common/constants/documents';
import { WATERFALL_PAGE_KEY } from 'common/constants/notes';
import { useFormat } from 'common/hooks';
import { useStore } from 'common/store';
import { CapTableVersionWithCompanyId, ChildCompany, CompanyInfo } from 'common/types/company';
import { UseFormatValues } from 'common/types/format';
import { UseStoreValues } from 'common/types/store';
import { CustomCompactContainer, CustomContainer } from 'components';
import WidgetDrawer from 'components/Drawer/widget-drawer/WidgetDrawer';
import { WidgetDrawerApplicationContext } from 'components/Drawer/widget-drawer/WidgetDrawer.props';
import EmptyTableMessage from 'components/EmptyTableMessage';
import { REGULAR_UNIT } from 'components/FeaturedSpreadsheet/constants';
import LoadingIndicator from 'components/LoadingIndicator';
import ScalarSpreadsheet from 'components/ScalarSpreadsheet';
import useWorkbook from 'components/ScalarSpreadsheet/utilities/useWorkbook';
import WorkbookContext from 'components/ScalarSpreadsheet/utilities/WorkbookContext';
import LayoutContext, { useLayoutContext } from 'context/LayoutContext';
import { EvaluatedCell, ScalarSpreadsheetConfigDataInterface } from 'models/interfaces/spreadsheet-types/core-types';
import { NeedsFundOwnershipFirst } from 'pages/Allocation/allocations/components';
import { getValidSecurities, useGenerateChildrenCompaniesCapTablesVersions } from 'pages/CapTable/cap-table/utilities';
import { DownloadWaterfall, WaterfallTables } from 'pages/Waterfall/components';
import WaterfallContext from 'pages/Waterfall/data/WaterfallContext/WaterfallContext';
import {
  CAP_TABLE_ALIAS,
  MEASUREMENT_DATE_ALIAS,
} from 'pages/Waterfall/SpreadsheetConfig/constants';
import {
  FundExchangeRateContext
} from 'pages/Waterfall/SpreadsheetConfig/FundExchangeRate/FundExchangeRateContext/FundExchangeRateContext';
import {
  useFundExchangeRateSheet
} from 'pages/Waterfall/SpreadsheetConfig/FundExchangeRate/useCreateFundExchangeRateSheet/useFundExchangeRateSheet';
import {
  useFundExchangeRate
} from 'pages/Waterfall/SpreadsheetConfig/FundExchangeRate/useFundExchangeRate/useFundExchangeRate';
import useFundInvestmentSheetConfig
  from 'pages/Waterfall/SpreadsheetConfig/FundInvestmentSheetConfig/useFundInvestmentSheetConfig/useFundInvestmentSheetConfig';
import {
  useWaterfallFundSummarySheetConfig
} from 'pages/Waterfall/SpreadsheetConfig/FundSummarySheetConfig/useFundSummarySheetConfig/useWaterfallFundSummarySheetConfig';
import useCreateInputSheet
  from 'pages/Waterfall/SpreadsheetConfig/InputSheetConfig/useCreateInputSheet/useCreateInputSheet';
import useWaterfallSheetConfig
  from 'pages/Waterfall/SpreadsheetConfig/WaterfallSheetConfig/useWaterfallSheetConfig/useWaterfallSheetConfig';
import { WaterfallDisplayProps } from 'pages/Waterfall/WaterfallDisplay/WaterfallDisplay.props';
import { useCompanyMeasurementDateFilter, useDocuments } from 'services/hooks';
import { useGetAllocationStatusBreadcrumb } from 'services/hooks/allocation';
import { useGetCompanyInfoById } from 'services/hooks/company';
import { useNotes } from 'services/hooks/notes';
import useSpreadsheetNotes from 'services/hooks/spreadsheetNotes/useSpreadsheetNotes';
import { getBreadcrumbs } from 'utilities';
import RelatedEntityWaterfallSheets from '../components/RelatedEntityWaterfallSheets/RelatedEntityWaterfallSheets';
import { useWaterfallComputedValues } from '../hooks/useWaterfallComputedValues/useWaterfallComputedValues';
import { useWaterfallData } from '../hooks/useWaterfallData/useWaterfallData';
import { useWaterfallDistribution } from '../hooks/useWaterfallDistribution/useWaterfallDistribution';
import { useWaterfallInputHandlers } from '../hooks/useWaterfallInputHandlers/useWaterfallInputHandlers';
import { useWaterfallIrr } from '../hooks/useWaterfallIrr/useWaterfallIrr';
import { useWaterfallUrlDefaults } from '../hooks/useWaterfallUrlDefaults/useWaterfallUrlDefaults';
import { useWaterfallUrlState } from '../hooks/useWaterfallUrlState/useWaterfallUrlState';
import useFirmTotalSheetConfig
  from '../SpreadsheetConfig/FirmInvestmentSheetConfig/UseFirmTotalSheetConfig/useFirmTotalSheetConfig';
import { useRelatedEntitiesWaterfalls } from '../utilities';

const WATERFALL_CONTAINER_CLASS = `${WATERFALL_SLUG}-container`;
const WATERFALL_INPUT_CONTAINER_CLASS = `${WATERFALL_SLUG}-input-container`;
const WATERFALL_SUMMARY_CONTAINER_CLASS = `${WATERFALL_SLUG}-summary-container`;
const WATERFALL_RELATED_ENTITY_CONTAINER_CLASS = `${WATERFALL_SLUG}-related-entity-container`;

const WaterfallDisplay = ({ isFirmPage }: WaterfallDisplayProps) => {
  // ============================================================
  // External hooks (documents, notes, store, etc.)
  // ============================================================
  const { filesToSave, setFilesToSave, addReferenceForExistingDocuments } = useDocuments();
  const { notes, setNotes, notesHasChanged, saveNotes, onAddNote, onUpdateNotes, onDeleteNote } = useNotes();
  const { spreadsheetNotes, setSpreadsheetNotes, saveSpreadsheetNotes, hasChanged: spreadsheetNoteHasChanged, handleOnDeleteNote: handleOnDeleteSpreadsheetNote } = useSpreadsheetNotes();
  const getCompanyInfoById = useGetCompanyInfoById();
  const { selectedMeasurementDate: selectedMeasurementDateFromRoute, MDDialogue, measurementDates } = useCompanyMeasurementDateFilter();
  const [{ companyInfo, firmInfo, fundList, companyList }] = useStore() as UseStoreValues;
  const { id: companyIdFromRoute, child_companies } = companyInfo as CompanyInfo;

  const { updatePageActions } = useLayoutContext();
  const { setPageBreadcrumbs } = useContext(LayoutContext);
  const { allocationStatusBreadcrumb } = useGetAllocationStatusBreadcrumb();

  // ============================================================
  // URL State (Source of Truth)
  // ============================================================
  const urlState = useWaterfallUrlState({
    isFirmPage: isFirmPage ?? false,
    companyList,
    capTableList: undefined, // Will be set after data loads
    companyMeasurementDates: undefined, // Will be set after data loads
  });

  // Determine effective company ID (from URL for firm page, from route for company page)
  const effectiveCompanyId = isFirmPage ? urlState.companyIdFromUrl : companyIdFromRoute;

  // ============================================================
  // Data Fetching (depends on URL state)
  // ============================================================
  const waterfallData = useWaterfallData({
    companyId: effectiveCompanyId,
    measurementDateValue: urlState.measurementDateValueFromUrl,
    captableId: urlState.captableIdFromUrl,
    selectedMeasurementDateFromRoute,
    isFirmPage: isFirmPage ?? false,
  });

  // Re-create URL state with loaded data for proper resolution
  const urlStateWithData = useWaterfallUrlState({
    isFirmPage: isFirmPage ?? false,
    companyList,
    capTableList: waterfallData.capTableList,
    companyMeasurementDates: waterfallData.companyMeasurementDates,
  });

  // ============================================================
  // Initialize URL Defaults
  // ============================================================
  const { isInitialized, isInitializing } = useWaterfallUrlDefaults({
    urlState: urlStateWithData,
    isFirmPage: isFirmPage ?? false,
    mostRecentMeasurementDate: waterfallData.mostRecentMeasurementDate,
    primaryCaptable: waterfallData.primaryCaptable,
    companyMeasurementDates: waterfallData.companyMeasurementDates,
    capTableList: waterfallData.capTableList,
    isMeasurementDatesLoading: waterfallData.isMeasurementDatesLoading,
    isCapTableListLoading: waterfallData.isCapTableListLoading,
    companyIdFromRoute,
    selectedMeasurementDateFromRoute,
  });

  // ============================================================
  // Computed Values
  // ============================================================
  const computedValues = useWaterfallComputedValues({
    equityValue: urlStateWithData.equityValueFromUrl,
    enterpriseValue: urlStateWithData.enterpriseValueFromUrl,
    cashOverride: urlStateWithData.cashOverrideFromUrl,
    debtOverride: urlStateWithData.debtOverrideFromUrl,
    financials: waterfallData.financials,
  });

  // ============================================================
  // Derived values for compatibility with existing code
  // ============================================================
  const selectedCompanyId = effectiveCompanyId;
  // IMPORTANT: For firm pages, always use URL state as source of truth
  // For company pages, use resolvedMeasurementDate from route context
  const companySelectedMeasurementDate = isFirmPage 
    ? urlStateWithData.selectedMeasurementDate 
    : (waterfallData.resolvedMeasurementDate || urlStateWithData.selectedMeasurementDate);
  const selectedCaptable = urlStateWithData.captableIdFromUrl;

  const exitDate = urlStateWithData.exitDateFromUrl ?? null;
  const equityValue = urlStateWithData.equityValueFromUrl ?? null;
  const enterpriseValue = urlStateWithData.enterpriseValueFromUrl ?? null;
  const {captableDetail} = waterfallData;
  const {capTableList} = waterfallData;
  const {companyMeasurementDates} = waterfallData;

  const { calculatedEquityValue, effectiveCash, effectiveDebt, cashIsFromFinancials, debtIsFromFinancials } = computedValues;

  // ============================================================
  // Local State (UI-specific, not in URL)
  // ============================================================
  const [findIrrMap, setFindIrrMap] = useState<{ [key: number]: number }>({});
  const [childrenCapTables, setChildrenCapTables] = useState<CapTableVersionWithCompanyId[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const fundInvestmentsSheetsNamesRef = useRef<string[]>([]);
  const mainCapTableRef = useRef<number | null>(null);
  const mainFundOwnershipRef = useRef<typeof captableDetail extends { fund_ownership: infer T } ? T : null>(null);


  // ============================================================
  // Callbacks for saving
  // ============================================================
  const saveDrawer = useCallback(() => {
    if (!isEmpty(filesToSave) && companySelectedMeasurementDate && selectedCaptable) {
      addReferenceForExistingDocuments(
        Number(companySelectedMeasurementDate.id),
        filesToSave,
        Number(selectedCaptable),
        CAP_TABLE_REFERENCE_TYPE
      );
    }

    if (!isEmpty(notes) && notesHasChanged) {
      saveNotes();
    }

    if (!isEmpty(spreadsheetNotes) && spreadsheetNoteHasChanged) {
      saveSpreadsheetNotes();
    }
  }, [
    filesToSave,
    companySelectedMeasurementDate,
    selectedCaptable,
    notes,
    notesHasChanged,
    spreadsheetNotes,
    spreadsheetNoteHasChanged,
    addReferenceForExistingDocuments,
    saveNotes,
    saveSpreadsheetNotes
  ]);

  // ============================================================
  // Page Actions
  // ============================================================
  useEffect(() => {
    if (companySelectedMeasurementDate?.cmd_id) {
      updatePageActions?.({
        mainAction: {
          id: 'waterfall',
          label: 'Save Notes & Documents',
          isActive: false,
          callback: saveDrawer,
          isLock: false,
        },
      });
    }

    return () => {
      updatePageActions?.(null);
    };
  }, [companySelectedMeasurementDate, updatePageActions, saveDrawer]);

  // ============================================================
  // Children Companies
  // ============================================================
  const childrenCompanies = useMemo(() => {
    if (child_companies) {
      return child_companies;
    }
    return (companyList ?? []).find(c => c.company_id === selectedCompanyId)?.child_companies ?? null;
  }, [child_companies, companyList, selectedCompanyId]);

  // ============================================================
  // Valid Securities (needed for distribution hook)
  // ============================================================
  const validSecurities = useMemo(() => {
    if (captableDetail) {
      return getValidSecurities(captableDetail);
    }
    return [];
  }, [captableDetail]);

  // ============================================================
  // Waterfall Distribution
  // ============================================================
  const { scenarios, setScenarios, updatedWeightedShareValues } = useWaterfallDistribution({
    selectedCaptable,
    equityValue: calculatedEquityValue,
    exitDate,
    captableDetail,
    capTableList,
    isInitializedFromUrl: isInitialized,
    isInitializingFromUrl: !isInitialized,
    selectedCompanyId,
    fundList,
    childrenCompanies,
    childrenCapTables,
    validSecurities,
  });

  // ============================================================
  // Portfolio/Related Entity Logic
  // ============================================================
  const isSelectedPortfolioPrimaryCapTable = useMemo(() => {
    if (!selectedCaptable || isEmpty(capTableList)) {
      return false;
    }
    return Boolean(capTableList.some(captable => captable.id === selectedCaptable && captable.is_primary));
  }, [selectedCaptable, capTableList]);

  const shouldRunRelatedEntitiesWaterfall = useMemo(
    () => isSelectedPortfolioPrimaryCapTable && (childrenCompanies ?? []).length > 0,
    [isSelectedPortfolioPrimaryCapTable, childrenCompanies]
  );

  const { generateChildrenCompaniesCapTablesVersions } = useGenerateChildrenCompaniesCapTablesVersions(
    companyList,
    selectedMeasurementDateFromRoute?.date ?? companySelectedMeasurementDate?.date,
    selectedCompanyId ? Number(selectedCompanyId) : undefined
  );

  const selectedCompanyInformation = useMemo(() => {
    if (isSelectedPortfolioPrimaryCapTable) {
      return (companyList ?? []).find(ct => ct.company_id === selectedCompanyId);
    }
    const childCompanyId = childrenCapTables.find(ct => ct.id === selectedCaptable);
    return (childrenCompanies ?? []).find(child => Number(child.company_id) === Number(childCompanyId?.company_id));
  }, [
    companyList,
    selectedCompanyId,
    isSelectedPortfolioPrimaryCapTable,
    childrenCapTables,
    childrenCompanies,
    selectedCaptable,
  ]);

  useEffect(() => {
    const fetchAndSetChildrenCapTables = async () => {
      const result = await generateChildrenCompaniesCapTablesVersions();
      const allChildrenCapTables = Object.values(result).flat();
      setChildrenCapTables(allChildrenCapTables.filter(ct => ct.is_primary));
    };
    fetchAndSetChildrenCapTables();
  }, [generateChildrenCompaniesCapTablesVersions]);

  // ============================================================
  // Sync cap table URL when cap table list changes
  // If the current URL cap table is not in the new list, default to primary
  // ============================================================
  useEffect(() => {
    // Skip if cap table list is still loading or empty
    if (waterfallData.isCapTableListLoading || !capTableList.length) {
      return;
    }

    const currentCaptableId = urlStateWithData.captableIdFromUrl;

    // If URL has a cap table, check if it's in the current list (including children)
    if (currentCaptableId !== undefined) {
      const isInMainList = capTableList.some(ct => ct.id === currentCaptableId);
      const isInChildrenList = childrenCapTables.some(ct => ct.id === currentCaptableId);
      if (isInMainList || isInChildrenList) {
        return;
      } // Cap table is valid, nothing to do
    }

    // URL cap table is not in the list (or URL has no cap table) - set to primary
    const primaryCaptable = capTableList.find(ct => ct.is_primary);
    if (primaryCaptable) {
      urlStateWithData.setCaptable(primaryCaptable);
    }
  }, [capTableList, childrenCapTables, waterfallData.isCapTableListLoading, urlStateWithData.captableIdFromUrl, urlStateWithData.setCaptable, urlStateWithData]);

  // ============================================================
  // Format & Exchange Rate
  // ============================================================
  const {
    format: fundFormat,
    formatDispatch: fundFormatDispatch,
    fundExchangeRateProvider,
    fundExchangeRateSheet,
    isAdditionalCurrencySelected
  } = useFundExchangeRate({
    page: CAP_TABLE_CURRENCY_PAGE,
    units: REGULAR_UNIT,
    measurementDate: companySelectedMeasurementDate,
    marketDate: exitDate ?? null,
    captable: captableDetail,
  });

  const [format, formatDispatch] = useFormat({
    page: CAP_TABLE_CURRENCY_PAGE,
    units: REGULAR_UNIT,
    measurementDate: companySelectedMeasurementDate,
  }) as unknown as UseFormatValues;

  // ============================================================
  // Company Info Fetch
  // ============================================================
  useEffect(() => {
    if (selectedCompanyId && (isEmpty(companyInfo) || selectedCompanyId !== companyInfo?.id)) {
      getCompanyInfoById(selectedCompanyId);
    }
  }, [selectedCompanyId, companyInfo, getCompanyInfoById]);

  // ============================================================
  // Main Cap Table Ref
  // ============================================================
  useEffect(() => {
    if (capTableList && capTableList.length > 0) {
      const primaryCapTable = capTableList.find(captable => captable.is_primary);
      mainCapTableRef.current = primaryCapTable?.id ?? null;
    }
  }, [capTableList]);

  // Update main fund ownership ref when captable detail changes
  useEffect(() => {
    if (captableDetail && mainCapTableRef.current === selectedCaptable) {
      mainFundOwnershipRef.current = captableDetail.fund_ownership || null;
    }
  }, [captableDetail, selectedCaptable]);

  // ============================================================
  // Breadcrumbs
  // ============================================================
  useEffect(() => {
    if (!isEmpty(companyInfo) && setPageBreadcrumbs && firmInfo && companyInfo) {
      setPageBreadcrumbs(
        getBreadcrumbs({
          firmInfo,
          companyInfo: companyInfo as unknown as Company,
          breadcrumbsTitle: 'Waterfall',
          extraBreadcrumbs: [allocationStatusBreadcrumb],
        })
      );
    }
    return () => setPageBreadcrumbs?.(null);
  }, [companyInfo, firmInfo, setPageBreadcrumbs, allocationStatusBreadcrumb]);

  // ============================================================
  // Firm Input Data
  // ============================================================
  const firmInputData = useMemo(() => {
    if (isFirmPage) {
      return {
        companyId: selectedCompanyId,
        measurementDateId: companySelectedMeasurementDate?.id,
      };
    }
  }, [isFirmPage, selectedCompanyId, companySelectedMeasurementDate]);

  // ============================================================
  // Sheet Configurations
  // ============================================================
  const { sheetConfig: waterfallSheet } = useWaterfallSheetConfig({
    scenarioObject: scenarios,
    validSecurities,
    companyName: selectedCompanyInformation?.company_name ?? '',
  });

  const { spreadsheets: fundInvestmentSheets } = useFundInvestmentSheetConfig({
    weightedShareValues: updatedWeightedShareValues,
    validSecurities,
    idToAssociateTo: null,
  });

  const {
    ownersShareValuesMap,
    relatedEntitiesWaterfalls,
    childrenSheets,
    registerChildrenSheets,
    resetChildrenSheets,
  } = useRelatedEntitiesWaterfalls({
    updatedWeightedShareValues,
    childrenCapTables,
    childrenCompanies: childrenCompanies ?? [],
    exitDate: exitDate ?? '',
    fundList: fundList ?? [],
    mainFundOwnershipRef,
  });

  const { sheetConfig: summarySheetConfig } = useWaterfallFundSummarySheetConfig({
    captable: captableDetail,
    fundList,
    childrenCompanies,
    calculatedEquityValue,
    findIrrMap,
  });

  const { spreadsheet: firmTotalSheet } = useFirmTotalSheetConfig({
    weightedShareValues: updatedWeightedShareValues,
    securityCount: validSecurities.length,
    investmentsInPortfolioSheets: fundInvestmentSheets,
    allInvestmentsSheets: [...fundInvestmentSheets, ...Object.values(childrenSheets).flat()],
    isSelectedPortfolioPrimaryCapTable,
    fundInvestmentsSheetsNamesRef,
    waterfallHasRunAtLeastOnce: Boolean(scenarios),
  });

  const inputSheet = useCreateInputSheet({
    exitDate,
    // Pass as string to match dropdown option values
    capTableId: selectedCaptable !== undefined ? String(selectedCaptable) : undefined,
    equityValue,
    enterpriseValue,
    firmOptions: firmInputData,
    childrenCapTables,
    parentCapTables: capTableList,
    fundInvestmentsSheetsNamesRef,
    plusCashIsFromFinancials: cashIsFromFinancials,
    lessDebtIsFromFinancials: debtIsFromFinancials,
    plusCash: effectiveCash,
    lessDebt: effectiveDebt,
    isAdditionalCurrencySelected
  });

  const sheets = useMemo(() => {
    if (shouldRunRelatedEntitiesWaterfall && Object.values(childrenSheets).flat().length > 0) {
      return [
        inputSheet,
        fundExchangeRateSheet,
        waterfallSheet,
        ...fundInvestmentSheets,
        ...Object.values(childrenSheets).flat(),
        firmTotalSheet,
        summarySheetConfig,
      ].filter(sheet => !!sheet);
    }
    return [
      inputSheet,
      fundExchangeRateSheet,
      waterfallSheet,
      ...fundInvestmentSheets,
      firmTotalSheet,
      summarySheetConfig,
    ].filter(sheet => !!sheet);
  }, [
    fundExchangeRateSheet,
    inputSheet,
    waterfallSheet,
    fundInvestmentSheets,
    childrenSheets,
    firmTotalSheet,
    shouldRunRelatedEntitiesWaterfall,
    summarySheetConfig,
  ]);

  const { cells: workbookCells, data: workbookData, onChange, workbookContextValue } = useWorkbook(sheets);

  useFundExchangeRateSheet({ cells: workbookCells, onChange, exchangeRate: fundExchangeRateProvider.exchangeRate });

  // ============================================================
  // Sync URL state to spreadsheet cells
  // When URL changes (e.g., via defaults), update the corresponding cells
  // ============================================================
  useEffect(() => {
    if (!workbookCells?.InputSheet) {
      return;
    }

    // Sync cap table value from URL to cell (pass as string to match dropdown option values)
    const capTableCell = Object.values(workbookCells.InputSheet).find(c => c.alias === CAP_TABLE_ALIAS);
    if (capTableCell && selectedCaptable !== undefined) {
      const currentValue = capTableCell.value;
      const selectedCaptableStr = String(selectedCaptable);
      // Only update if the value is different (avoid infinite loops)
      if (String(currentValue) !== selectedCaptableStr) {
        onChange(capTableCell, selectedCaptableStr);
      }
    }

    // Sync measurement date value from URL to cell (for firm pages, pass as string)
    if (isFirmPage) {
      const measurementDateCell = Object.values(workbookCells.InputSheet).find(c => c.alias === MEASUREMENT_DATE_ALIAS);
      const resolvedMeasurementDateId = urlStateWithData.selectedMeasurementDate?.id;
      if (measurementDateCell && resolvedMeasurementDateId !== undefined) {
        const currentValue = measurementDateCell.value;
        const measurementDateIdStr = String(resolvedMeasurementDateId);
        if (String(currentValue) !== measurementDateIdStr) {
          onChange(measurementDateCell, measurementDateIdStr);
        }
      }
    }
  }, [workbookCells, selectedCaptable, urlStateWithData.selectedMeasurementDate?.id, isFirmPage, onChange]);

  // ============================================================
  // IRR Data
  // ============================================================
  const { isInputsLoaded } = useWaterfallIrr({
    workbookCells,
    selectedCaptable,
    exitDate,
    onChange,
    setFindIrrMap,
    isFirmPage: isFirmPage ?? false,
    companyList,
    isFinancialsLoading: waterfallData.isFinancialsLoading,
  });

  // ============================================================
  // Input Handlers
  // ============================================================
  const { onWorkbookChange } = useWaterfallInputHandlers({
    urlState: urlStateWithData,
    onChange,
    workbookCells,
    setScenarios,
    resetChildrenSheets,
    capTableList,
    childrenCapTables,
    companyMeasurementDates,
  });

  // ============================================================
  // Loading State
  // ============================================================
  useEffect(() => {
    if (isFirmPage) {
      setIsLoading(!companyList?.length);
    }
  }, [companyList, isFirmPage]);

  const combinedIsLoading = isLoading || waterfallData.isLoading || isInitializing;

  // ============================================================
  // Context Values
  // ============================================================
  const waterfallContextValues = useMemo(
    () => ({
      companyList: companyList ?? [],
      measurementDates: companyMeasurementDates,
      capTableList,
      workbookData,
      onChange,
      format,
      formatDispatch,
    }),
    [companyList, companyMeasurementDates, capTableList, workbookData, onChange, format, formatDispatch]
  );

  // ============================================================
  // Render
  // ============================================================
  return (
    <>
      <WorkbookContext.Provider value={workbookContextValue}>
        <FundExchangeRateContext.Provider value={fundExchangeRateProvider}>
          <WaterfallContext.Provider value={waterfallContextValues}>
            <CustomContainer className={WATERFALL_CONTAINER_CLASS}>
              <CustomContainer>
                {workbookData && inputSheet?.data && isInputsLoaded && (
                  <>
                    <Grid className={WATERFALL_INPUT_CONTAINER_CLASS} id="waterfall-inputs" size={{ xs: 12 }}>
                      <ScalarSpreadsheet
                        {...inputSheet}
                        format={fundFormat}
                        showToolbar
                        formatDispatch={fundFormatDispatch}
                        onChange={onWorkbookChange}
                        disableUnsavedChanges
                        disableVerticalScroll
                        sheet={inputSheet}
                      />
                    </Grid>

                    {summarySheetConfig && (
                      <Grid className={WATERFALL_SUMMARY_CONTAINER_CLASS} size={{ xs: 12 }}>
                        <ScalarSpreadsheet
                          {...summarySheetConfig}
                          format={format}
                          showToolbar
                          formatDispatch={formatDispatch}
                          onChange={onWorkbookChange}
                          disableUnsavedChanges
                          disableVerticalScroll
                          sheet={summarySheetConfig}
                        />{' '}
                      </Grid>
                    )}
                  </>
                )}
              </CustomContainer>

              {waterfallSheet && workbookData && updatedWeightedShareValues && (
                <CustomContainer>
                  <CustomCompactContainer>
                    <CustomContainer>
                      <Grid size={{ xs: 12 }}>
                        <WaterfallTables
                          waterfallSheet={waterfallSheet as ScalarSpreadsheetConfigDataInterface}
                          fundInvestmentsSheets={fundInvestmentSheets}
                          firmTotalSheet={
                            Object.entries(ownersShareValuesMap ?? {}).flat().length ? undefined : firmTotalSheet
                          }
                        />
                      </Grid>

                      {shouldRunRelatedEntitiesWaterfall &&
                        ownersShareValuesMap &&
                        Object.entries(ownersShareValuesMap).map(([relatedEntityId, sharesValue]) => (
                          <Grid className={WATERFALL_RELATED_ENTITY_CONTAINER_CLASS} key={relatedEntityId}>
                            <RelatedEntityWaterfallSheets
                              relatedEntity={
                                (childrenCompanies ?? []).find(
                                  child => Number(child.company_id) === Number(relatedEntityId)
                                ) ?? ({} as ChildCompany)
                              }
                              scenarioResult={relatedEntitiesWaterfalls?.[Number(relatedEntityId)] as Scenario}
                              childrenCapTables={childrenCapTables}
                              ownedSharesValue={sharesValue}
                              registerChildrenSheets={registerChildrenSheets}
                            />
                          </Grid>
                        ))}
                    </CustomContainer>

                    {exitDate &&
                      (equityValue || calculatedEquityValue) &&
                      companySelectedMeasurementDate &&
                      selectedCompanyInformation &&
                      selectedCaptable ? (
                        <DownloadWaterfall
                          exitDate={exitDate}
                          equityValue={equityValue ?? (calculatedEquityValue as number)}
                          companyMeasurementDate={companySelectedMeasurementDate}
                          selectedCapTableId={selectedCaptable}
                          selectedCompanyId={Number(selectedCompanyInformation.company_id)}
                        />
                      ) : null}
                  </CustomCompactContainer>
                </CustomContainer>
              )}

              <LoadingIndicator isLoading={combinedIsLoading} />

              {validSecurities?.length === 0 && scenarios !== undefined && selectedCaptable && (
                <EmptyTableMessage
                  title="The selected company has an incomplete cap table"
                  tableTerms={{
                    tableName: 'Cap table',
                    pluralColumnName: 'Securities',
                    columnName: 'Security',
                  }}
                />
              )}

              {updatedWeightedShareValues && isEmpty(updatedWeightedShareValues) && (
                <NeedsFundOwnershipFirst
                  measurementDates={measurementDates}
                  selectedMeasurementDate={selectedMeasurementDateFromRoute}
                  title="No fund ownership defined for the cap table"
                  tagline="Add fund ownership to see its share values"
                />
              )}
              <MDDialogue />
            </CustomContainer>
          </WaterfallContext.Provider>
        </FundExchangeRateContext.Provider>
      </WorkbookContext.Provider>
      {companySelectedMeasurementDate && selectedCaptable && (
        <WidgetDrawer
          applicationContext={WidgetDrawerApplicationContext.CAP_TABLE}
          notesProps={{
            pageType: SpreadsheetNote.page_type.WTRFL,
            pageTypeKey: WATERFALL_PAGE_KEY,
            pageTypeId: companySelectedMeasurementDate.cmd_id,
            notes,
            setNotes,
            onAddNote,
            onUpdateNotes,
            onDeleteNote,
            isDisabled: false,
          }}
          documentReferencesProps={{
            selectedMeasurementDate: companySelectedMeasurementDate,
            referenceType: CAP_TABLE_REFERENCE_TYPE,
            currentPage: CAP_TABLE_REFERENCE_TITLE,
            referencedFeatureId: selectedCaptable,
            filesToSave,
            setFilesToSave,
            isDisplayingRowNumbers: false,
            isDisabled: false,
          }}
          spreadsheetNotesProps={{
            pageTypeId: companySelectedMeasurementDate.cmd_id as number,
            pageType: SpreadsheetNote.page_type.WTRFL,
            pageTypeKey: WATERFALL_PAGE_KEY,
            notes: spreadsheetNotes,
            setNotes: setSpreadsheetNotes,
            saveNotes: () => {
              saveSpreadsheetNotes();
            },
            handleOnDeleteNote: handleOnDeleteSpreadsheetNote,
          }}
        />
      )}
    </>
  );
};

export default WaterfallDisplay;
